import apiGraph from "@/helpers/api/apiGraph";
import apiSharePoint from "@/helpers/api/apiSharePoint";
import Permission from "@/models/Documents/Permission";

import TrackHelper from "@/helpers/utilities/TrackHelper";

export const permissions_state = {
  itemPermissionsMap: {} // { driveId: { itemId: { permissions-for-me }}},
};

export const permissions_actions = {
  /**
   * Load permissions for driveItem (only loads if not in store)
   * @param { string } driveId DriveId to load permissions for
   * @param { string } itemId ItemId to load permissions for
   * @returns { object } Permission object
   */

  async loadDocumentPermissions(driveId, itemId) {
    const calcItemId = this.calculateRootOrItemId(driveId, itemId);

    if (!this.myItemPermission(driveId, calcItemId)) {
      await this.reloadDocumentPermissions(driveId, itemId);
    }

    return this.myItemPermission(driveId, calcItemId);
  },

  /**
   * Reload permissions for driveItem
   * @param { string } driveId DriveId to load permissions for
   * @param { string } itemId ItemId to load permissions for
   * @returns { object } Permission object
   */
  async reloadDocumentPermissions(driveId, itemId) {
    if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
      const response = await apiGraph.get(`/drives/${driveId}/items/${itemId}/permissions`).catch(() => {
        const map = Object.assign({}, this.itemPermissionsMap);
        if (!map[driveId]) {
          map[driveId] = {};
        }

        map[driveId][itemId] = new Permission(null, null, null, Permission.nctPermission.None);
        this.itemPermissionsMap = map;
      });

      if (response && response.data) {
        const map = Object.assign({}, this.itemPermissionsMap);
        if (!map[driveId]) {
          map[driveId] = {};
        }

        map[driveId][itemId] = calculatePermissionsFromSharePointPermissionObject(response.data);
        this.itemPermissionsMap = map;
      }
    } else {
      let driveItem = await this.loadDriveItem(driveId, itemId);

      if (driveItem && driveItem.webUrl && driveItem.webUrl.length > 0) {
        const regexSite = /^((https?:\/\/?[^:/]*\/sites\/[^:/]*\/)|(https?:\/\/?[^:/]*)).*$/g; // https://regex101.com/r/rEiXyO/6
        const regexSiteAndFiles = /(\/\/[a-zA-Z0-9.-]+)([/sites|personal]?\/[^?]+)/g; // https://regex101.com/r/UVox6L/5
        const regexForSharePointFolderUrl = /^https:\/\/([^/]+)(\/(?:personal|sites)\/[^/]+)((?:\/[^/]+)*)$/; // https://regex101.com/r/W5vQFK/1

        const regexForSharePointFileUrl =
          /^https:\/\/([^/]+)(\/(?:personal|sites)\/[^/]+)((?:\/[^/?]+)*)(?:\?[^#]*?\bsourcedoc=%7B([^&#]+))?%7D/;

        // https://viborgskoler15.sharepoint.com/sites/DAGP-G16708/F%C3%A6llesdrev/Led/LedAktive
        // https://stackoverflow.com/questions/22264002/how-to-get-permission-levels-for-files-folders-documents-in-sharepoint-using-res

        let result;
        if (driveItem.webUrl.includes("/personal/")) {
          if ((driveItem.folder || driveItem.isFolder) && !driveItem.root && !driveItem.package) {
            const urlSplit = regexForSharePointFolderUrl.exec(driveItem.webUrl);

            result = await apiSharePoint.get(
              `https://${urlSplit[1]}${urlSplit[2]}/_api/web/getFolderByServerRelativeUrl('${urlSplit[2]}${urlSplit[3]}')/ListItemAllFields/EffectiveBasePermissions`
            );
          } else if (driveItem.root) {
            const urlSplit = regexForSharePointFolderUrl.exec(driveItem.webUrl);
            result = await apiSharePoint.get(`https://${urlSplit[1]}${urlSplit[2]}/_api/web/EffectiveBasePermissions`);
          } else {
            const urlSplitO365File = regexForSharePointFileUrl.exec(driveItem.webUrl);
            if (urlSplitO365File) {
              result = await apiSharePoint.get(
                `https://${urlSplitO365File[1]}${urlSplitO365File[2]}/_api/web/getfilebyid('${urlSplitO365File[4]}')/ListItemAllFields/EffectiveBasePermissions`
              );
            } else {
              const urlSplitOtherFile = regexForSharePointFolderUrl.exec(driveItem.webUrl);
              result = await apiSharePoint.get(
                `https://${urlSplitOtherFile[1]}${urlSplitOtherFile[2]}/_api/web/getFolderByServerRelativeUrl('${urlSplitOtherFile[2]}${urlSplitOtherFile[3]}')/ListItemAllFields/EffectiveBasePermissions`
              );
            }
          }
        } else {
          if ((driveItem.folder || driveItem.isFolder) && !driveItem.root && !driveItem.package) {
            const urlSplit = regexForSharePointFolderUrl.exec(driveItem.webUrl);

            result = await apiSharePoint.get(
              `https://${urlSplit[1]}${urlSplit[2]}/_api/web/getFolderByServerRelativeUrl('${urlSplit[2]}${urlSplit[3]}')/ListItemAllFields/EffectiveBasePermissions`
            );
          } else if (driveItem.root) {
            // Log these urls to find out, which urls is the problem
            TrackHelper.registerMessage("Root drive item url: " + driveItem.webUrl);

            const urlSplit = regexSite.exec(driveItem.webUrl);
            const urlSplitFiles = regexSiteAndFiles.exec(driveItem.webUrl);
            const res = await apiSharePoint.get(
              `${urlSplit[1]}/_api/web/getFolderByServerRelativeUrl('${urlSplitFiles[2]}')/Properties`
            );
            if (res && res.data && res.data.vti_x005f_listtitle) {
              result = await apiSharePoint.get(
                `${urlSplit[1]}/_api/web/getListByTitle('${res.data.vti_x005f_listtitle}')/EffectiveBasePermissions`
              );
            }
          } else {
            const urlSplitO365File = regexForSharePointFileUrl.exec(driveItem.webUrl);
            if (urlSplitO365File) {
              result = await apiSharePoint.get(
                `https://${urlSplitO365File[1]}${urlSplitO365File[2]}/_api/web/getfilebyid('${urlSplitO365File[4]}')/ListItemAllFields/EffectiveBasePermissions`
              );
            } else {
              const urlSplitOtherFile = regexForSharePointFolderUrl.exec(driveItem.webUrl);
              result = await apiSharePoint.get(
                `https://${urlSplitOtherFile[1]}${urlSplitOtherFile[2]}/_api/web/getFolderByServerRelativeUrl('${urlSplitOtherFile[2]}${urlSplitOtherFile[3]}')/ListItemAllFields/EffectiveBasePermissions`
              );
            }
          }
        }

        if (result && result.data) {
          const map = Object.assign({}, this.itemPermissionsMap);
          if (!map[driveId]) {
            map[driveId] = {};
          }

          map[driveId][itemId] = calculatePermissionsFromSharePointPermissionObject(result.data);
          this.itemPermissionsMap = map;
        }
      } else {
        const map = Object.assign({}, this.itemPermissionsMap);
        if (!map[driveId]) {
          map[driveId] = {};
        }
        map[driveId][itemId] = new Permission();
        this.itemPermissionsMap = map;
      }
    }
    return this.itemPermissionsMap[driveId][itemId];
  }
};

export const permissions_getters = {
  myItemPermission(state) {
    return (driveId, itemId) => {
      const realDriveId = this.calculateRootOrDriveId(driveId);
      const realItemId = itemId;

      if (state.itemPermissionsMap[realDriveId]) {
        if (state.itemPermissionsMap[realDriveId][realItemId]) {
          return state.itemPermissionsMap[realDriveId][realItemId];
        }
      }
      return null;
    };
  }
};

/**
 * Calculates permission to from SharePoint permission object
 * @param { object } permissionObject Permission object from SharePoint EffectiveBasePermissions result
 * @returns { object } Permission object
 */
function calculatePermissionsFromSharePointPermissionObject(permissionObject) {
  if ((permissionObject.Low & (1 << (4 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.OWNER);
  } else if ((permissionObject.Low & (1 << (3 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.WRITE);
  } else if ((permissionObject.Low & (1 << (1 - 1))) != 0) {
    return new Permission(null, null, null, Permission.nctPermission.READ);
  } else {
    return new Permission(null, null, null, Permission.nctPermission.None);
  }
}
